/**
 * Keeps an authenticated screen honest about inactivity (see `lib/inactivity.ts`).
 *
 *  · While the person interacts, the session is renewed a little before its access token ends,
 *    and only then: if nothing was touched since the last renewal, nothing is renewed.
 *  · From two minutes before the limit the screen warns, and at the limit it signs the person
 *    out. Every tab of the browser reads the same clock, so they warn and sign out together
 *    and an interaction in any of them keeps all of them alive.
 *  · A move to another screen counts as interaction (the first screen of a page load does not).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { accessTokenLifetimeMs } from '../lib/accessToken';
import { inactivity, renewalDue } from '../lib/inactivity';
import authService from '../services/auth.service';
import { readPersistedAccessToken } from '../stores/authStore';

export interface IdleSession {
  /** Seconds until sign-out while the warning is showing; null otherwise. */
  secondsLeft: number | null;
  /** "Stay signed in": counts as interaction and renews the session now. */
  stay: () => void;
}

export function useIdleSession(): IdleSession {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const lastAttempt = useRef(Number.NEGATIVE_INFINITY);
  const location = useLocation();
  const previousScreen = useRef(location.key);

  useEffect(() => {
    if (previousScreen.current === location.key) return;
    previousScreen.current = location.key;
    inactivity.markInteraction();
  }, [location.key]);

  const renew = useCallback((): void => {
    lastAttempt.current = Date.now();
    authService.refreshWhileActive().catch(() => undefined);
  }, []);

  useEffect(() => {
    const renewIfActive = (): void => {
      const token = readPersistedAccessToken();
      const due = renewalDue({
        mayRefresh: inactivity.mayRefresh(),
        now: Date.now(),
        refreshedAt: inactivity.lastRefreshAt(),
        lifetimeMs: token ? accessTokenLifetimeMs(token) : null,
        lastAttemptAt: lastAttempt.current,
      });
      if (due) renew();
    };
    return inactivity.watch(
      globalThis,
      {
        setInterval: (handler, ms) => globalThis.setInterval(handler, ms),
        clearInterval: (handle) => globalThis.clearInterval(handle as number),
      },
      {
        onTick: (phase, left) => {
          setSecondsLeft(phase === 'warning' ? left : null);
          if (phase !== 'idle') renewIfActive();
        },
        onIdle: () => authService.signOutForInactivity(),
        // Another tab signed out: the stored sign-in is gone, so this tab ends too.
        signedOut: () => readPersistedAccessToken() === null,
      },
    );
  }, [renew]);

  const stay = useCallback((): void => {
    inactivity.markInteraction(true);
    setSecondsLeft(null);
    renew();
  }, [renew]);

  return { secondsLeft, stay };
}
